import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OutcomesProvider } from "@/context/OutcomesContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import CalendarPage from "@/pages/CalendarPage";
import WorkflowPage from "@/pages/WorkflowPage";
import CollaboratorsPage from "@/pages/CollaboratorsPage";
import AppointmentTypesPage from "@/pages/AppointmentTypesPage";
import OutcomesPage from "@/pages/OutcomesPage";
import ClientsPage from "@/pages/ClientsPage";
import StatisticsPage from "@/pages/StatisticsPage";
import SettingsPage from "@/pages/SettingsPage";
import ExportPage from "@/pages/ExportPage";
import ReminderPage from "@/pages/ReminderPage";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Caricamento…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function DefaultRedirect() {
  const { user } = useAuth();
  const target = user?.role === "trainer" ? "/calendario-trainers" : "/calendario";
  return <Navigate to={target} replace />;
}

function RoleRoute({ allow, redirectTo = "/", children }) {
  const { user } = useAuth();
  if (user && !allow.includes(user.role)) return <Navigate to={redirectTo} replace />;
  return children;
}

function BlockRole({ blockRole, redirectTo, children }) {
  const { user } = useAuth();
  if (user?.role === blockRole) return <Navigate to={redirectTo} replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <Protected>
                  <OutcomesProvider>
                    <AppShell />
                  </OutcomesProvider>
                </Protected>
              }
            >
              <Route index element={<DefaultRedirect />} />
              <Route path="calendario" element={
                <BlockRole blockRole="trainer" redirectTo="/calendario-trainers">
                  <CalendarPage calendarType="consulenti" />
                </BlockRole>
              } />
              <Route path="calendario-trainers" element={<CalendarPage calendarType="trainers" />} />
              <Route path="flusso-lavoro" element={
                <BlockRole blockRole="trainer" redirectTo="/flusso-trainers">
                  <WorkflowPage calendarType="consulenti" />
                </BlockRole>
              } />
              <Route path="flusso-trainers" element={<WorkflowPage calendarType="trainers" />} />
              <Route path="collaboratori" element={
                <BlockRole blockRole="trainer" redirectTo="/calendario-trainers">
                  <BlockRole blockRole="receptionist" redirectTo="/calendario">
                    <BlockRole blockRole="collaborator" redirectTo="/calendario">
                      <CollaboratorsPage />
                    </BlockRole>
                  </BlockRole>
                </BlockRole>
              } />
              <Route path="tipi-appuntamento" element={
                <RoleRoute allow={["admin"]}><AppointmentTypesPage /></RoleRoute>
              } />
              <Route path="esiti" element={
                <RoleRoute allow={["admin"]}><OutcomesPage /></RoleRoute>
              } />
              <Route path="clienti" element={<ClientsPage />} />
              <Route path="promemoria" element={
                <RoleRoute allow={["admin","manager","receptionist"]}><ReminderPage /></RoleRoute>
              } />
              <Route path="statistiche" element={<StatisticsPage />} />
              <Route path="esportazione" element={
                <RoleRoute allow={["admin","manager","receptionist"]}><ExportPage /></RoleRoute>
              } />
              <Route path="impostazioni" element={<SettingsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
